import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PROPERTY_TYPES, FREQUENCIES, SERVICE_OPTIONS } from "@/lib/content";

const initial = {
  full_name: "", phone: "", email: "", suburb: "", property_type: "",
  service: "", preferred_date: "", preferred_time: "", frequency: "",
  property_size: "", message: "",
};

const inputCls =
  "focus-ring w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-teal-500";

const TIME_OPTIONS = ["Morning (8am – 12pm)", "Afternoon (12pm – 4pm)", "Evening (4pm – 7pm)", "Flexible"];

const Field = ({ label, htmlFor, required, error, children }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-semibold text-navy">
      {label} {required && <span className="text-teal-600" aria-hidden="true">*</span>}
    </label>
    {children}
    {error && (
      <p className="mt-1.5 text-xs font-medium text-red-600" role="alert" data-testid={`${htmlFor}-error`}>
        {error}
      </p>
    )}
  </div>
);

export default function QuoteForm({ tid = "quote", heading = true }) {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.full_name.trim()) er.full_name = "Please enter your full name.";
    if (!form.phone.trim()) er.phone = "Please enter your phone number.";
    else if (!/^[+0-9][0-9 ()\-]{6,19}$/.test(form.phone.trim())) er.phone = "Please enter a valid phone number.";
    if (!form.email.trim()) er.email = "Please enter your email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) er.email = "Please enter a valid email address.";
    if (!form.service) er.service = "Please select a cleaning service.";
    if (!form.message.trim()) er.message = "Please tell us a little about what you need cleaned.";
    return er;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const er = validate();
    if (Object.keys(er).length) {
      setErrors(er);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const mapped = {};
        detail.forEach((d) => {
          const key = d.loc?.[d.loc.length - 1];
          if (key) mapped[key] = d.msg;
        });
        setErrors(mapped);
      } else {
        toast.error("Something went wrong sending your enquiry. Please try again or call us.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-3xl border border-teal-200 bg-teal-50 p-10 text-center" data-testid={`${tid}-form-success`}>
        <CheckCircle2 className="mx-auto h-12 w-12 text-teal-600" />
        <h3 className="mt-5 font-display text-2xl font-bold text-navy">Enquiry Received</h3>
        <p className="mx-auto mt-3 max-w-md text-slate-600">
          Thank you for contacting RD Cleaning Services. We've received your enquiry and will get back to you as soon as possible.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-navy/5 sm:p-10">
      {heading && (
        <div className="mb-8">
          <p className="eyebrow">Free Quote</p>
          <h2 className="mt-2 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">
            Get Your Free Cleaning Quote
          </h2>
          <p className="mt-2 text-sm text-slate-600 sm:text-base">
            Tell us what you need cleaned and we'll get back to you with a suitable cleaning solution.
          </p>
        </div>
      )}
      <form onSubmit={onSubmit} noValidate className="grid gap-5 sm:grid-cols-2" data-testid={`${tid}-form`}>
        <Field label="Full Name" htmlFor={`${tid}-full-name`} required error={errors.full_name}>
          <input id={`${tid}-full-name`} data-testid={`${tid}-full-name-input`} className={inputCls} value={form.full_name} onChange={set("full_name")} autoComplete="name" placeholder="Jane Citizen" />
        </Field>
        <Field label="Phone Number" htmlFor={`${tid}-phone`} required error={errors.phone}>
          <input id={`${tid}-phone`} data-testid={`${tid}-phone-input`} type="tel" className={inputCls} value={form.phone} onChange={set("phone")} autoComplete="tel" placeholder="04XX XXX XXX" />
        </Field>
        <Field label="Email Address" htmlFor={`${tid}-email`} required error={errors.email}>
          <input id={`${tid}-email`} data-testid={`${tid}-email-input`} type="email" className={inputCls} value={form.email} onChange={set("email")} autoComplete="email" placeholder="you@example.com" />
        </Field>
        <Field label="Suburb" htmlFor={`${tid}-suburb`} error={errors.suburb}>
          <input id={`${tid}-suburb`} data-testid={`${tid}-suburb-input`} className={inputCls} value={form.suburb} onChange={set("suburb")} placeholder="e.g. Scarborough" />
        </Field>
        <Field label="Property Type" htmlFor={`${tid}-property-type`} error={errors.property_type}>
          <select id={`${tid}-property-type`} data-testid={`${tid}-property-type-select`} className={inputCls} value={form.property_type} onChange={set("property_type")}>
            <option value="">Select property type</option>
            {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Cleaning Service Required" htmlFor={`${tid}-service`} required error={errors.service}>
          <select id={`${tid}-service`} data-testid={`${tid}-service-select`} className={inputCls} value={form.service} onChange={set("service")}>
            <option value="">Select a service</option>
            {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Preferred Date" htmlFor={`${tid}-date`} error={errors.preferred_date}>
          <input id={`${tid}-date`} data-testid={`${tid}-date-input`} type="date" className={inputCls} value={form.preferred_date} onChange={set("preferred_date")} />
        </Field>
        <Field label="Preferred Time" htmlFor={`${tid}-time`} error={errors.preferred_time}>
          <select id={`${tid}-time`} data-testid={`${tid}-time-select`} className={inputCls} value={form.preferred_time} onChange={set("preferred_time")}>
            <option value="">Select a time</option>
            {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Frequency" htmlFor={`${tid}-frequency`} error={errors.frequency}>
          <select id={`${tid}-frequency`} data-testid={`${tid}-frequency-select`} className={inputCls} value={form.frequency} onChange={set("frequency")}>
            <option value="">Select frequency</option>
            {FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </Field>
        <Field label="Approximate Property / Facility Size" htmlFor={`${tid}-size`} error={errors.property_size}>
          <input id={`${tid}-size`} data-testid={`${tid}-size-input`} className={inputCls} value={form.property_size} onChange={set("property_size")} placeholder="e.g. 3-bedroom house / 200 sqm office" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Message / Additional Requirements" htmlFor={`${tid}-message`} required error={errors.message}>
            <textarea id={`${tid}-message`} data-testid={`${tid}-message-input`} rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about your space and what you'd like cleaned…" />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <button type="submit" disabled={submitting} className="focus-ring inline-flex w-full items-center justify-center gap-2 rounded-full bg-teal-600 px-8 py-4 text-base font-bold text-white shadow-lg shadow-teal-600/25 transition-all hover:-translate-y-0.5 hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto" data-testid={`${tid}-submit-button`}>
            {submitting && <Loader2 className="h-5 w-5 animate-spin" />}
            {submitting ? "Sending…" : "Request My Free Quote"}
          </button>
        </div>
      </form>
    </div>
  );
}
